// tslint:disable:max-file-line-count
import { ICommercialCardProps } from '@cian/api-models/search-card-fragment/v1/get-commercial-card.props';
import { Fragment, IFragmentProps } from '@cian/fragment/shared';
import * as React from 'react';

import { isExperimentActive } from '../../../api/experiments';
import { IJsonQuery, IJsonQueryUnderground } from '../../../json_query';
import { EMaxAuctionService, IBreadcrumb, IOffer } from '../../../offer/types';
import { isCommercial as isCommercialList, offerTypeFromJsonQuery } from '../../../utils/category';
import { IMakeRequest } from '../../../utils/request';
import { addAdFoxBanner, getBannerMiddleware } from '../adfox';
import { ICommercialCardComplaintsHelpers, prepareCommercialCardComponentsProps } from './commercial_card';
import { TOffersProps } from './index';
import { CoworkingProfitBanner } from './internal/components/CoworkingProfitBanner';
import { prepareOfferCardComponentsProps } from './offer_card';
import {
  getTrackingParamsOfferPull,
  makeOfferAnalyticsClick,
  trackFavoriteAdded,
  trackFavoriteRemoved,
  trackPageJumped,
  trackPhoneOpened,
  trackPhotoSlideChanged,
  trackTradePopupClick as makeTradePopupClick,
  trackTradePopupShow as makeTradePopupShow,
} from './tracking';
import { EHideOfferState } from '../../state/hide_offer';
import { trackHiddenObjects } from '../../../tracking/hide_offer';
import { IABExperiment } from '@cian/api-models/monolith-python/cian-api/site/v1/get-ab-experiments';
import { sendTeaserEvent } from '../../../../browser/utils/TeaserEvent';
import { withObservation } from '@cian/component-observer';
import { ObservableBannerServicePlace } from '../service_places/banner/container';
import { NewbuildingConsultBannerConatiner } from '../NewbuildingConsultBanner';
import { showTzarCard } from './showTzarCard';
import { CountrysideTrapBanner } from '../CountrysideTrapBanner';
import { OfferContainer } from '../../../../fragments/OfferCardWidget/shared/containers/OfferContainer';
import { shouldSendAdsEvent } from '../../../utils/shouldSendAdsEvent';
import { IAbUseExperiment } from '../../../types/abUseExperiments';
import { isDealRentPrepaymentActive } from '../../../utils/experiments/dealRentPrepayment';

const styles = require('./index.css');

type TSuggestionFragment = { isSimilar: false } | { isSimilar: true; startIndex: number };
type TCommercialCardProps = ICommercialCardProps &
  IFragmentProps &
  IFlocktoryPushNotification & {
    complaintsHelpers: ICommercialCardComplaintsHelpers;
    maxAuctionBet: number;
    /** Максимальная услуга выдачи */
    maxAuctionService?: EMaxAuctionService;
    currentPageNumber: number;
    jsonQuery: IJsonQuery;
    CoworkingProfitBanner?: React.ComponentType;
    isOfficeSearch?: boolean;
    abUseExperiments?: IAbUseExperiment[];
  };

export interface IIntegrateAdOptions {
  currentPageNumber: number;
  perPage: number;
  jsonQuery: IJsonQuery;
  offers: IOffer[];
  offerCardCreator(
    offer: IOffer,
    position: number,
    index: number,
    currentPageNumber: number,
    keyPrefix: string,
  ): React.ReactNode;
  isPrintEnabled: boolean;
  suggestionFragment: TSuggestionFragment;
  makeRequest: IMakeRequest;
  hiddenOffers?: number[];
  hiddenSuggestions?: number[];
  animatingOffer?: number | null;
  locationId?: number;
  isCommercial?: boolean;
  positionOffset?: number;
  extendedOffersOffset?: number;
  priceRange?: string;
  experiments: IABExperiment[];
  mlRankingGuid: string | null;
  isAgent?: boolean;
  villagesLink?: string;
  showCountrysideTrap?: boolean;
  keyPrefix: string;
  isQaMode?: boolean;
  newbuildingConsultBannerAvailable?: boolean;
  trackShowNBMotivationPopup?: boolean;
  motivationPopupPosition?: number;
  realtyUserId?: number | null;
}

interface IFlocktoryPushNotification {
  showPushNotification?: boolean;
}

type TObservableFragment = Pick<
  IOfferCardCreatorProps,
  | 'userGADataLayerData'
  | 'currentPageNumber'
  | 'deviceType'
  | 'queryString'
  | 'jsonQuery'
  | 'isDealRentPrepaymentExperimentActive'
> &
  Pick<TCommercialCardProps, 'offer' | 'contentId'> & { soprApi: string; observerKey?: string | number };

export const transformTrapProps = (props: TObservableFragment) => ({
  soprApi: props.soprApi,
  user_id: (props.userGADataLayerData && props.userGADataLayerData.realtyUserId) || null,
  announcement_id: (props.offer as IOffer).id,
  pagenumber: props.jsonQuery.page ? props.jsonQuery.page.value : 1,
  position: (props.contentId || 0) + 1,
  pageType: 'Listing',
  device_type: props.deviceType,
  ab_group: (props.userGADataLayerData && props.userGADataLayerData.abGroup) || null,
  block_type: 'offer',
  sc: props.queryString,
});

// tslint:disable-next-line: no-any
const ObservableFragment = withObservation<TObservableFragment>(Fragment as any, {
  onChange: (events: Array<{ props: TObservableFragment; isViewed: boolean }>) =>
    sendTeaserEvent(
      events.map(({ props, isViewed }) => ({ ...transformTrapProps(props), isTeaserShow: isViewed ? 1 : 0 })),
    ),
});

// tslint:disable-next-line: no-any
const ObservabLocalFragment = withObservation<TObservableFragment>(OfferContainer as any, {
  /* istanbul ignore next */
  onChange(events: Array<{ props: TObservableFragment; isViewed: boolean }>) {
    /* istanbul ignore next */
    sendTeaserEvent(
      events.map(({ props, isViewed }) => ({ ...transformTrapProps(props), isTeaserShow: isViewed ? 1 : 0 })),
    );
  },
});

export const integrateAd = (options: IIntegrateAdOptions) => {
  const {
    currentPageNumber,
    perPage,
    offers,
    offerCardCreator,
    isPrintEnabled,
    makeRequest,
    locationId,
    isCommercial,
    suggestionFragment,
    jsonQuery,
    priceRange,
    hiddenOffers = [],
    hiddenSuggestions = [],
    isAgent,
    villagesLink,
    showCountrysideTrap,
    extendedOffersOffset,
    keyPrefix,
    isQaMode,
    newbuildingConsultBannerAvailable,
    realtyUserId,
  } = options;

  let positionOffset = options.positionOffset || 0;

  return offers.reduce((acc: React.ReactNode[], offer: IOffer, index) => {
    if (!hiddenOffers.includes(offer.id) && !hiddenSuggestions.includes(offer.id)) {
      const isOfferExtended = offer.extensionLabels && offer.extensionLabels.length;
      const position = (currentPageNumber - 1) * perPage + index;
      // порядок для события считаем начиная с единицы
      let trackingPosition = position + 1;

      // у добивок собственное начало отсчета
      trackingPosition = isOfferExtended ? trackingPosition - (extendedOffersOffset || 0) : trackingPosition;

      const additionalPuids = {
        ...getAdditionalAdFoxPuids(jsonQuery),
        puid6: priceRange || '',
        ...(isQaMode ? { puid32: 'test' } : {}),
      };

      positionOffset += 1;

      const generateMortgageBanner = (positionOffset: number) =>
        addAdFoxBanner({
          type: 'mortgage_in_listing',
          key: `${keyPrefix}-mortgage_in_listing-${offer.id}`,
          makeRequest,
          offer,
          isAgent,
          isCommercial,
        });

      const generateTgbBanner = (positionOffset: number) =>
        addAdFoxBanner({
          type: 'tgb_in_offers_list',
          key: `${keyPrefix}-tgb_in_offers_list-${offer.id}`,
          makeRequest,
          offer,
          locationId,
          isAgent,
          isCommercial,
          additionalPuids: {
            ...additionalPuids,
            ...getRailwayAndHighwayPuids(jsonQuery),
          },
          realtyUserId,
          shouldSendAdsEvent: shouldSendAdsEvent(jsonQuery),
        });
      const generateParallaxBanner = (positionOffset: number) =>
        addAdFoxBanner({
          type: 'parallax',
          key: `${keyPrefix}-parallax-${offer.id}`,
          makeRequest,
          offer,
          locationId,
          isAgent,
          isCommercial,
          additionalPuids: {
            ...additionalPuids,
            ...getRailwayAndHighwayPuids(jsonQuery),
          },
          isNew: true,
          shouldSendAdsEvent: shouldSendAdsEvent(jsonQuery),
        });

      acc.push(
        offerCardCreator(
          offer,
          trackingPosition,
          suggestionFragment.isSimilar ? index + suggestionFragment.startIndex : index,
          currentPageNumber,
          keyPrefix,
        ),
      );

      if (!isPrintEnabled) {
        if (positionOffset === 15) {
          acc.push(generateMortgageBanner(positionOffset));
        }

        // tslint:disable-next-line:prefer-switch]
        if (positionOffset === 5 || (positionOffset === 25 && offers.length > 25)) {
          acc.push(generateTgbBanner(positionOffset));
        }

        if (showCountrysideTrap && villagesLink && position === 1) {
          acc.push(<CountrysideTrapBanner key={`countryside_banner_${position}`} villagesLink={villagesLink} />);
        }

        if (offers.length !== positionOffset && [2, 32, 62].includes(position)) {
          // Определяем, нужно ли показывать баннер консультации по новостройкам
          if (newbuildingConsultBannerAvailable && !isAgent) {
            acc.push(<NewbuildingConsultBannerConatiner key={`banner_${position}`} />);
          } else {
            acc.push(<ObservableBannerServicePlace key={`banner_${position}`} options={options} position={position} />);
          }
        }

        if (positionOffset === 10 || positionOffset === 20) {
          acc.push(generateParallaxBanner(positionOffset));
        }
      }
    }

    return acc;
  }, []);
};

export const getOnFavoriteChange = (
  offer: IOffer,
  jsonQuery: IJsonQuery,
  position: number,
  onFavoriteChangedHandler: (offer: IOffer, isFavorite: boolean) => void,
) => (isFavorite: boolean) => {
  const trackingLabel = offer.gaLabel;

  if (isFavorite) {
    trackFavoriteAdded(jsonQuery, { offerId: offer.id, position }, trackingLabel || undefined);
  } else {
    trackFavoriteRemoved(jsonQuery, { offerId: offer.id, position }, trackingLabel || undefined);
  }

  onFavoriteChangedHandler(offer, isFavorite);
};

export interface IOfferCardCreatorProps extends TOffersProps {
  offersQty: number;
  queryString: string;
  priceRange?: string;
  defaultPhone?: string;
  defaultEmail?: string;
  showPushNotification?: boolean;
  breadcrumbs: IBreadcrumb[];
  isAgent?: boolean;
  soprApi: string;
  isRedesignEnabled: boolean;
}

export enum EOfferFragment {
  TOP_OFFER_CARD = 'topOfferCardFragment',
  OFFER_CARD = 'offerCardFragment',
  SLONO_TOP_CARD = 'slonoTopCardFragment',
}

interface IGetOfferCardCreator {
  props: IOfferCardCreatorProps;
  isSimilar?: boolean;
  isSimilarFromML?: boolean;
  pageNum?: number;
  isExtended?: boolean;
}

export const getOfferCardCreator = ({ props, isSimilar, isSimilarFromML, pageNum }: IGetOfferCardCreator) => (
  offer: IOffer,
  position: number,
  index: number,
  currentPageNumber: number,
  keyPrefix: string,
): React.ReactNode => {
  const {
    baseUrl,
    breadcrumbs,
    currentPath,
    currentSubdomain,
    experiments,
    onFavoriteChanged,
    makeRequest,
    isBot,
    isPrintEnabled,
    jsonQuery,
    onUserInfoPopupClose,
    onUserInfoPopupOpen,
    hideOfferAction,
    qsToUris,
    suggestionDistancesSeoText,
    user,
    userGADataLayerData,
    offersQty,
    queryString,
    priceRange,
    defaultPhone,
    defaultEmail,
    hideOffer: { hideOfferState, hidingOffer },
    showPushNotification,
    maxAuctionBet,
    maxAuctionService,
    isAgent,
    isLightView,
    isPromotionsInOffersActive,
    isOfferInSavedSearch,
    soprApi,
    deviceType,
    isQaMode,
    onAnalyticsButtonClick,
    isCianAgencyExperimentActive,
    trackingData,
    auctionBanks,
    abUseExperiments,
  } = props;

  const { id, bargainTerms } = offer;
  const withAuction = Boolean(
    !!auctionBanks && !!offer.user && !!offer.user.cianUserId && auctionBanks.includes(+offer.user.cianUserId),
  );
  const additionalPuids = {
    ...getAdditionalAdFoxPuids(jsonQuery),
    puid6: priceRange || '',
    ...(isQaMode ? { puid32: 'test' } : {}),
  };

  const offerType = offerTypeFromJsonQuery(jsonQuery);
  const onPopupClose = () => onUserInfoPopupClose(offer.id);
  const onPopupOpen = () => onUserInfoPopupOpen(offer.id);
  const onHideOffer =
    hideOfferState === EHideOfferState.FETCHING || hideOfferState === EHideOfferState.ANIMATING
      ? () => {}
      : () => {
          trackHiddenObjects({ action: 'Click_icon', offer, jsonQuery });
          hideOfferAction({ offer, isSimilar: !!isSimilar });
        };
  const isTitleExperimentActive = isExperimentActive(experiments, 'offer_title_for_vas');

  const FragmentComponent = isExperimentActive(experiments, 'card_visibility_observation')
    ? ObservableFragment
    : Fragment;

  const LocalFragmentComponent = isExperimentActive(experiments, 'card_visibility_observation')
    ? ObservabLocalFragment
    : OfferContainer;

  if (isCommercialList(offerType)) {
    const mortgageBanner = !isPrintEnabled
      ? addAdFoxBanner({
          type: 'mortgage_banner',
          key: `${keyPrefix}-mortgage_banner-${id}`,
          makeRequest,
          offer,
          additionalPuids,
          isAgent,
        })
      : null;

    const onPageJumped = (label: string) =>
      trackPageJumped(offer, {
        user,
        userGALayerData: userGADataLayerData,
        jsonQuery,
        position,
        isSimilarFromML,
        label,
        pageNum,
        breadcrumbs,
        queryString,
        offersQty,
      });

    /* istanbul ignore next */
    const trackTradePopupShow = () => {
      makeTradePopupShow(offer, {
        user,
        userGALayerData: userGADataLayerData,
        jsonQuery,
        position,
        pageNum,
        queryString,
        offersQty,
      });
    };

    /* istanbul ignore next */
    const trackTradePopupClick = () =>
      makeTradePopupClick(offer, {
        user,
        userGALayerData: userGADataLayerData,
        jsonQuery,
        position,
        pageNum,
        queryString,
        offersQty,
      });

    const trackOfferAnalyticsClick = () =>
      makeOfferAnalyticsClick(offer, {
        user,
        userGALayerData: userGADataLayerData,
        jsonQuery,
        position,
        pageNum,
        queryString,
        offersQty,
      });

    const onButtonClick = () => {
      if (onAnalyticsButtonClick) {
        trackOfferAnalyticsClick();
        onAnalyticsButtonClick(
          offer.id,
          getTrackingParamsOfferPull(offer, {
            user,
            userGALayerData: userGADataLayerData,
            jsonQuery,
            position,
            pageNum,
            queryString,
            offersQty,
          }),
        );
      }
    };

    const commercialCardProps = prepareCommercialCardComponentsProps(
      props,
      {
        mortgageBanner,
        onPageJumped,
        onPopupClose,
        onPopupOpen,
        onFavoriteChange: getOnFavoriteChange(offer, jsonQuery, position, onFavoriteChanged),
        onHideOffer,
        onPhoneOpened: () =>
          trackPhoneOpened(offer, position, jsonQuery, user, userGADataLayerData, offersQty, queryString, isSimilar),
        onPhotoSlideChangedOnce: () => trackPhotoSlideChanged(offer.id, position),
        trackTradePopupShow,
        trackTradePopupClick,
        analyticsPopup: {
          onButtonClick,
        },
      },
      {
        offer,
        user,
        userGALayerData: userGADataLayerData,
        jsonQuery,
        position,
        pageNum,
        queryString,
        offersQty,
      },
    );

    const isOfficeSearch = !!offer.coworkingOfferType && !jsonQuery.coworking_offer_type;

    const { complaintsHelpers, ...commercialCardApiProps } = commercialCardProps;

    const fragmentProps: TCommercialCardProps = {
      api: commercialCardApiProps,
      baseUrl,
      currentPath,
      currentSubdomain,
      contentId: index,
      experiments,
      fragmentClassName: styles['commercialWrapper'],
      isBot,
      logger: props.logger,
      isSimilar: !!isSimilar,
      jsonQuery,
      maxAuctionBet,
      maxAuctionService,
      offer,
      qsToUris: qsToUris || undefined,
      suggestionDistancesSeoText: isSimilar ? suggestionDistancesSeoText : undefined,
      type: 'commercialCardFragment',
      user,
      defaultPhone,
      defaultEmail,
      showPushNotification,
      isHideOfferFetching:
        Boolean(hidingOffer && hidingOffer.offer.id === offer.id) && hideOfferState === EHideOfferState.FETCHING,
      isOnHideAnimation:
        Boolean(hidingOffer && hidingOffer.offer.id === offer.id) && hideOfferState === EHideOfferState.ANIMATING,
      complaintsHelpers,
      currentPageNumber,
      CoworkingProfitBanner: isOfficeSearch ? CoworkingProfitBanner : React.Fragment,
      isOfficeSearch,
      abUseExperiments,
    };

    return (
      <FragmentComponent
        key={`${keyPrefix}-${id}`}
        soprApi={soprApi}
        deviceType={deviceType}
        userGADataLayerData={userGADataLayerData}
        queryString={queryString}
        observerKey={queryString}
        {...fragmentProps}
      />
    );
  }

  const mortgageBanner =
    bargainTerms && bargainTerms.mortgageAllowed && !isPrintEnabled
      ? addAdFoxBanner({
          type: 'mortgage_banner',
          key: `${keyPrefix}-mortgage_banner-${id}`,
          makeRequest,
          offer,
          additionalPuids,
          isAgent,
        })
      : null;

  const onPageJumped = (label: string) =>
    trackPageJumped(offer, {
      user,
      userGALayerData: userGADataLayerData,
      jsonQuery,
      position,
      isSimilarFromML,
      label,
      pageNum,
      breadcrumbs,
      queryString,
      offersQty,
    });

  const offerCardProps = prepareOfferCardComponentsProps(props, {
    mortgageBanner,
    onPageJumped,
    onPopupClose,
    onPopupOpen,
    onHideOffer,
    onFavoriteChange: getOnFavoriteChange(offer, jsonQuery, position, onFavoriteChanged),
    trackPhoneOpened: (isFromBrandBlock?: boolean) =>
      trackPhoneOpened(
        offer,
        position,
        jsonQuery,
        user,
        userGADataLayerData,
        offersQty,
        queryString,
        isSimilar,
        isFromBrandBlock,
      ),
    isTitleExperimentActive,
    offer,
    jsonQuery,
    position,
  });

  const { complaintsHelpers, ...offerCardApiProps } = offerCardProps;

  if (props.isRedesignEnabled) {
    return (
      <LocalFragmentComponent
        key={`${keyPrefix}-${id}`}
        currentPath={currentPath}
        suggestionDistancesSeoText={suggestionDistancesSeoText}
        position={position}
        soprApi={soprApi}
        deviceType={deviceType}
        trackingData={trackingData}
        userGADataLayerData={userGADataLayerData}
        queryString={queryString}
        observerKey={queryString}
        offer={offer}
        jsonQuery={jsonQuery}
        qsToUris={qsToUris}
        isCianDealRentExperiment={Boolean(isCianAgencyExperimentActive)}
        user={user}
        getMortgageInPriceAdFoxParams={
          bargainTerms && bargainTerms.mortgageAllowed && !isPrintEnabled
            ? getBannerMiddleware({
                type: 'mortgage_banner',
                offer,
                additionalPuids,
              })
            : undefined
        }
        mortgageBanner={mortgageBanner}
        excludedUtilitiesTermsRegions={offerCardProps.excludedUtilitiesTermsRegions}
        contentId={index}
        currentPageNumber={currentPageNumber}
        maxAuctionBet={maxAuctionBet}
        maxAuctionService={maxAuctionService}
        isHideOfferInProgress={
          (Boolean(hidingOffer && hidingOffer.offer.id === offer.id) && hideOfferState === EHideOfferState.FETCHING) ||
          (Boolean(hidingOffer && hidingOffer.offer.id === offer.id) && hideOfferState === EHideOfferState.ANIMATING)
        }
        api={{
          onAgentRatingChanged: offerCardProps.userInfo.onAgentRatingChanged,
          onPageJumped,
          onHideOffer: offerCardProps.buttons.onHideOffer,
          isPopupsOpened: props.isPopupsOpened,
          onFavoritesChange: offerCardProps.offerCard.onFavoriteChange,
          sendComplaintFeedback: offerCardProps.complaintsHelpers.sendComplaintFeedback,
          onComplaintSent: offerCardProps.reportPopup.onComplaintSent,
        }}
        isBot={offerCardProps.pessimisation.isBot}
        tracking={offerCardProps.tracking}
        featureToggle={offerCardProps.featureToggle}
        withAuction={withAuction}
        isDealRentPrepaymentExperimentActive={Boolean(
          offer.offerInDeal && isDealRentPrepaymentActive(props.abUseExperiments),
        )}
      />
    );
  }
  /*
      Условия брендирования VAS: отображение царь-карточки (PM-4302):
      Только десктоп
      Только Топ
    */
  const getFragmentType = (): EOfferFragment => {
    /**
     * Не добавлен в api models, т.к. эксперимент и потом сложно будет выпиливать
     */
    //todo выпилить, или внести в api-models
    //tslint:disable-next-line:no-any
    if ((offer as any).isSlonoTop) {
      return EOfferFragment.SLONO_TOP_CARD;
    }

    if (showTzarCard(offer)) {
      return EOfferFragment.TOP_OFFER_CARD;
    }

    return EOfferFragment.OFFER_CARD;
  };

  const fragmentType = getFragmentType();

  const fragmentProps = {
    api: offerCardApiProps,
    baseUrl,
    currentPath,
    currentSubdomain,
    contentId: index,
    experiments,
    fragmentClassName: styles['card'],
    logger: props.logger,
    isSimilar: !!isSimilar,
    jsonQuery,
    offer,
    position,
    qsToUris,
    suggestionDistancesSeoText: isSimilar ? suggestionDistancesSeoText : undefined,
    type: fragmentType,
    user,
    defaultPhone,
    defaultEmail,
    isHideOfferFetching:
      Boolean(hidingOffer && hidingOffer.offer.id === offer.id) && hideOfferState === EHideOfferState.FETCHING,
    isOnHideAnimation:
      Boolean(hidingOffer && hidingOffer.offer.id === offer.id) && hideOfferState === EHideOfferState.ANIMATING,
    showPushNotification,
    complaintsHelpers,
    maxAuctionBet,
    maxAuctionService,
    isLightView,
    isPromotionsInOffersActive,
    isOfferInSavedSearch,
    currentPageNumber,
    isCianAgencyExperimentActive,
  };

  return (
    <FragmentComponent
      key={`${keyPrefix}-${id}`}
      soprApi={soprApi}
      deviceType={deviceType}
      userGADataLayerData={userGADataLayerData}
      queryString={queryString}
      observerKey={queryString}
      {...fragmentProps}
    />
  );
};

export function getAdditionalAdFoxPuids(jsonQuery: IJsonQuery): { [key: string]: string } {
  const puid9 = (
    (jsonQuery.geo &&
      jsonQuery.geo.value
        .filter(geo => geo.type === 'underground')
        .map((underground: IJsonQueryUnderground) => underground.id)) ||
    []
  ).join(':');

  return {
    puid9,
  };
}

export function getRailwayAndHighwayPuids(jsonQuery: IJsonQuery): { [key: string]: string } {
  return jsonQuery.geo && jsonQuery.geo.value
    ? jsonQuery.geo.value.reduce(
        (acc, next) => {
          if (next.type === 'railway' && jsonQuery._type.includes('suburban')) {
            acc.puid34 = acc.puid34 ? `${acc.puid34}:${next.id}` : String(next.id);
          }

          if (next.type === 'highway' && jsonQuery._type.includes('suburban')) {
            acc.puid35 = acc.puid35 ? `${acc.puid35}:${next.id}` : String(next.id);
          }

          return acc;
        },
        { puid34: '', puid35: '' },
      )
    : {};
}

export function getOfferPrice(offer: IOffer) {
  let price = offer.minPriceTotalRur || offer.bargainTerms.priceRur;

  if (offer.bargainTerms.utilitiesTerms && !offer.bargainTerms.utilitiesTerms.includedInPrice) {
    price += offer.bargainTerms.utilitiesTerms.price;
  }

  return price;
}
